/**
 * @file schemas.ts
 * @brief Zod schemas for company-scoped sessions (`GET /api/sessions`).
 *
 * @author DES
 * @date 19-06-2026
 */

import { z } from "zod";
import {
  SessionKindSchema,
  SessionStatusSchema,
} from "@/features/sessions/schemas";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

export const CompanySessionRowSchema = z
  .object({
    session_id: z.string(),
    started_at: z.string().nullable().optional(),
    ended_at: z.string().nullable().optional(),
    duration_seconds: numericString.nullable().optional(),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    status: SessionStatusSchema,
    currency: z.string().nullable().optional(),
    total_cents: numericString.nullable().optional(),
    person_a_name: z.string().nullable().optional(),
    person_b_name: z.string().nullable().optional(),
    participant_first_name: z.string().nullable().optional(),
    participant_last_name: z.string().nullable().optional(),
    participant_email: z.string().nullable().optional(),
    owner_first_name: z.string().nullable().optional(),
    owner_last_name: z.string().nullable().optional(),
    owner_email: z.string().nullable().optional(),
    person_a_avatar_url: z.string().nullable().optional(),
    person_a_gender: z.string().nullable().optional(),
    person_b_gender: z.string().nullable().optional(),
    billing_context: z.string().nullable().optional(),
    reference_number: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    notes: z.string().nullable().optional(),
    session_kind: SessionKindSchema,
  })
  .passthrough();

export type CompanySessionRow = z.infer<typeof CompanySessionRowSchema>;

export const CompanySessionsResponseSchema = z.object({
  success: z.boolean().optional(),
  sessions: z.array(CompanySessionRowSchema).default([]),
  total: numericString.optional(),
  loaded: z.number().optional(),
  has_more: z.boolean().optional(),
  limit: z.number().optional(),
  offset: z.number().optional(),
});

export type CompanySessionsResponse = z.infer<
  typeof CompanySessionsResponseSchema
>;

export type CompanySessionsListParams = {
  page?: number;
  limit?: number;
  q?: string;
  from?: string;
  to?: string;
  employee_id?: string;
  status?: "in_progress" | "completed" | "failed" | "cancelled";
  kind?: "standard" | "realtime";
};

export const CompanySessionUtteranceSchema = z
  .object({
    speaker: z.string().nullable().optional(),
    original_text: z.string().nullable().optional(),
    translated_text: z.string().nullable().optional(),
    src_text: z.string().nullable().optional(),
    tgt_text: z.string().nullable().optional(),
    timestamp_ms: numericString.nullable().optional(),
    utterance_index: numericString.nullable().optional(),
    src_audio_url: z.string().nullable().optional(),
    tgt_audio_url: z.string().nullable().optional(),
  })
  .passthrough();

export const CompanySessionDetailSchema = z
  .object({
    session_id: z.string(),
    started_at: z.string().nullable().optional(),
    ended_at: z.string().nullable().optional(),
    duration_seconds: numericString.nullable().optional(),
    source_language: z.string().nullable().optional(),
    target_language: z.string().nullable().optional(),
    status: SessionStatusSchema,
    currency: z.string().nullable().optional(),
    total_cents: numericString.nullable().optional(),
    total_cost_cents: numericString.nullable().optional(),
    person_a_name: z.string().nullable().optional(),
    person_b_name: z.string().nullable().optional(),
    person_a_avatar_url: z.string().nullable().optional(),
    billing_context: z.string().nullable().optional(),
    reference_number: z.string().nullable().optional(),
    tags: z.array(z.string()).nullable().optional(),
    notes: z.string().nullable().optional(),
    session_kind: SessionKindSchema.optional(),
    transcript_url: z.string().nullable().optional(),
    invoice_url: z.string().nullable().optional(),
    utterances: z.array(CompanySessionUtteranceSchema).default([]),
  })
  .passthrough();

export type CompanySessionDetail = z.infer<typeof CompanySessionDetailSchema>;

export const CompanySessionDetailResponseSchema = z.object({
  success: z.boolean().optional(),
  session: CompanySessionDetailSchema,
});

export function sessionEmployeeLabel(row: CompanySessionRow): string {
  const ownerName = [row.owner_first_name, row.owner_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (ownerName) return ownerName;
  if (row.owner_email) return row.owner_email;
  const name = [row.participant_first_name, row.participant_last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (name) return name;
  if (row.participant_email) return row.participant_email;
  if (row.person_a_name) return row.person_a_name;
  return "—";
}

export function sessionEmployeeKey(row: CompanySessionRow): string {
  const email = row.owner_email ?? row.participant_email;
  if (email) return email.toLowerCase();
  return sessionEmployeeLabel(row).toLowerCase();
}
